/**
 * M2 P6: procedural audio (Web Audio API — no asset files). The engine exposes
 * master + sub-bus gains so the Ledger settings screen can drive real levels.
 */
import type { AudioSettings } from '../settings/types.js';
import { SfxSampleBank, type SfxEvent, type SfxLoopId } from './sfxSamples.js';
import { createRadioChain, type RadioChain } from './radioChain.js';

const HUM_BASE_HZ = 46;
const HUM_RANGE_HZ = 70;
const HUM_MAX_GAIN = 0.12;

type BusName = 'master' | 'music' | 'sfx' | 'ui' | 'ambience' | 'dialogue';

/** One aggregated boot notice of absent sampled cues. Player-facing audio stays
 * silent (these degrade to silence); this is a single console.info for developers,
 * never an error, so a partial asset wave is visible without breaking the build. */
const reportMissingAudio = (missing: readonly string[]): void => {
  if (missing.length === 0) return;
  console.info(
    `[audio] ${missing.length} sampled cue(s) absent — silent fallback: ${missing.join(', ')}`,
  );
};

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private buses: Partial<Record<BusName, GainNode>> = {};
  private humOsc: OscillatorNode | null = null;
  private humGain: GainNode | null = null;
  private humFilter: BiquadFilterNode | null = null;
  private samples: SfxSampleBank | null = null;
  private started = false;
  // Ambient DUCK node between the ambient bed (hum + engine loops) and the ambience
  // bus, so music/VO ducking can pull the bed down without fighting the volume slider.
  private ambienceDuckGain: GainNode | null = null;
  // Comms sources feed dialogueInputGain; it routes either through the radio chain
  // or straight to the dialogue bus depending on the RADIO FILTER setting.
  private dialogueInputGain: GainNode | null = null;
  private radio: RadioChain | null = null;
  private radioOn = true;
  private settings: AudioSettings = {
    master: 0.9,
    music: 0.7,
    sfx: 0.9,
    ui: 0.85,
    ambience: 0.85,
    dialogue: 0.9,
    muted: false,
    radioFilter: true,
  };

  resume(): void {
    if (!this.ctx) this.init();
    void this.ctx?.resume();
  }

  applySettings(settings: AudioSettings): void {
    this.settings = { ...settings };
    this.syncGains();
    if (settings.radioFilter !== this.radioOn) {
      this.radioOn = settings.radioFilter;
      this.routeDialogue();
    }
  }

  setThrottle(throttle: number): void {
    this.samples?.setEngine(throttle);
    if (!this.ctx || !this.humOsc || !this.humGain) return;
    const t = Math.min(1, Math.abs(throttle));
    const now = this.ctx.currentTime;
    this.humOsc.frequency.setTargetAtTime(HUM_BASE_HZ + HUM_RANGE_HZ * t, now, 0.08);
    this.humGain.gain.setTargetAtTime(HUM_MAX_GAIN * (0.25 + 0.75 * t), now, 0.08);
  }

  setFlight(throttle: number, speed01: number, boosting: boolean): void {
    this.samples?.setEngine(throttle);
    if (!this.ctx || !this.humOsc || !this.humGain || !this.humFilter) return;
    const t = Math.min(1, Math.abs(throttle));
    const sp = Math.max(0, Math.min(1, speed01));
    const drive = Math.max(t, sp);
    const now = this.ctx.currentTime;
    this.humOsc.frequency.setTargetAtTime(
      HUM_BASE_HZ + HUM_RANGE_HZ * drive + (boosting ? 30 : 0),
      now,
      0.08,
    );
    this.humGain.gain.setTargetAtTime(HUM_MAX_GAIN * (0.25 + 0.75 * drive), now, 0.08);
    this.humFilter.frequency.setTargetAtTime(320 + 1300 * sp + (boosting ? 1600 : 0), now, 0.1);
  }

  boost(): void {
    if (this.samples?.play('boost')) return;
    this.blip('sawtooth', 180, 0.5, 0.14, 'sfx', 720);
  }

  fire(): void {
    if (this.samples?.play('fire')) return;
    this.blip('square', 880, 0.09, 0.18, 'sfx', 420);
  }

  hit(): void {
    if (this.samples?.play('impact')) return;
    this.blip('square', 240, 0.07, 0.16, 'sfx', 90);
    this.blip('triangle', 520, 0.04, 0.08, 'sfx');
  }

  incomingHit(): void {
    if (this.samples?.play('shieldHit')) return;
    this.blip('sawtooth', 150, 0.18, 0.2, 'sfx', 60);
    this.blip('sine', 80, 0.22, 0.12, 'sfx', 55);
  }

  kill(): void {
    // Sampled kill-confirm first; procedural double-tap stays as the fallback.
    if (this.samples?.play('killConfirm')) return;
    this.blip('triangle', 330, 0.05, 0.07, 'sfx');
    this.blip('triangle', 220, 0.1, 0.06, 'sfx', 160);
  }

  dock(): void {
    if (this.samples?.play('dock')) return;
    this.blip('triangle', 520, 0.18, 0.16, 'ui');
  }

  /** Undock clamp release. Sampled cue first; a soft procedural clamp as fallback. */
  undock(): void {
    if (this.samples?.play('undockClamp')) return;
    this.blip('triangle', 300, 0.16, 0.12, 'ui', 200);
  }

  uiClick(): void {
    this.blip('sine', 660, 0.05, 0.08, 'ui');
  }

  death(): void {
    this.blip('sine', 130, 1.2, 0.07, 'ambience', 90);
  }

  /** Dead-stick drift bed — layers UNDER the 130 Hz death tone. Sample or silence. */
  deathDriftBed(): void {
    this.samples?.play('deathDriftBed');
  }

  lockAcquired(): void {
    if (this.samples?.play('lockAcquiredV2')) return;
    this.blip('triangle', 440, 0.06, 0.04, 'ui');
    this.blip('triangle', 660, 0.06, 0.03, 'ui');
  }

  waveInbound(): void {
    if (this.samples?.play('waveInboundV2')) return;
    this.blip('sine', 110, 0.35, 0.06, 'ui', 180);
  }

  recordTruth(): void {
    if (this.samples?.play('verifyTruth')) return;
    this.blip('triangle', 410, 0.16, 0.05, 'ui', 560);
  }

  recordContradiction(): void {
    if (this.samples?.play('verifyContradiction')) return;
    this.blip('sine', 210, 0.22, 0.06, 'ui', 140);
  }

  /** Scoop capture. Sampled cue first; the UI click (today's scoop sound) as fallback. */
  scoopCapture(): void {
    if (this.samples?.play('scoopCapture')) return;
    this.uiClick();
  }

  // --- Menu shell nav cues (sample-or-silence; assets arrive in a later wave) ---
  uiTick(): void {
    this.samples?.play('uiTick');
  }

  uiConfirm(): void {
    if (this.samples?.play('uiConfirm')) return;
    this.uiClick(); // keep the existing procedural confirm as fallback
  }

  uiBack(): void {
    this.samples?.play('uiBack');
  }

  uiDenied(): void {
    this.samples?.play('uiDenied');
  }

  // --- Supercruise lifecycle cues (sample-or-silence; client-side hooks only) ---
  cruiseSpool(): void {
    this.samples?.play('cruiseSpool');
  }

  cruiseEngage(): void {
    this.samples?.play('cruiseEngage');
  }

  cruiseDrop(): void {
    this.samples?.play('cruiseDrop');
  }

  cruiseTunnelStart(): void {
    this.samples?.startLoop('cruiseTunnel');
  }

  cruiseTunnelStop(): void {
    this.samples?.stopLoop('cruiseTunnel');
  }

  // --- Hull-critical alarm loop (sample-or-silence) ---
  hullCriticalStart(): void {
    this.samples?.startLoop('hullCritical');
  }

  hullCriticalStop(): void {
    this.samples?.stopLoop('hullCritical');
  }

  /** Audio-page loop preview: start/stop a named loop directly. */
  previewLoopStart(id: SfxLoopId): boolean {
    return this.samples?.startLoop(id) ?? false;
  }

  previewLoopStop(id: SfxLoopId): void {
    this.samples?.stopLoop(id);
  }

  toggleMute(): void {
    this.applySettings({ ...this.settings, muted: !this.settings.muted });
  }

  /** Play a sampled one-shot directly through its bus (used by the audio page).
   * Returns false when the buffer is absent so the caller can show "absent". */
  previewSample(event: SfxEvent): boolean {
    return this.samples?.play(event) ?? false;
  }

  get ready(): boolean {
    return this.started;
  }

  get audioContext(): AudioContext | null {
    return this.ctx;
  }

  get musicBus(): GainNode | null {
    return this.buses.music ?? null;
  }

  get sfxBus(): GainNode | null {
    return this.buses.sfx ?? null;
  }

  get ambienceBus(): GainNode | null {
    return this.buses.ambience ?? null;
  }

  /** Duck node in front of the ambience bus — musicManager pulls this down under VO. */
  get ambienceDuck(): GainNode | null {
    return this.ambienceDuckGain ?? null;
  }

  /** CAMPAIGN VO: campaign voice-over bus (routed through master, so master
   * volume + mute already fold it). Kept for the audio-page preview path. */
  get dialogueBus(): GainNode | null {
    return this.buses.dialogue ?? null;
  }

  /** CAMPAIGN VO: the node the comms panel feeds its decoded clips into. Routes
   * through the radio chain (band-pass + compression) when RADIO FILTER is on,
   * then out to the dialogue bus. Null until the context is resumed. */
  get dialogueInput(): AudioNode | null {
    return this.dialogueInputGain ?? this.buses.dialogue ?? null;
  }

  private init(): void {
    const Ctor = window.AudioContext ?? (window as unknown as {
      webkitAudioContext?: typeof AudioContext;
    }).webkitAudioContext;
    if (!Ctor) return;
    this.ctx = new Ctor();
    this.buses.master = this.createGain();
    this.buses.music = this.createBus('master');
    this.buses.sfx = this.createBus('master');
    this.buses.ui = this.createBus('master');
    this.buses.ambience = this.createBus('master');
    // Ambient bed ducks under music/VO via this node (default 1.0 = no duck).
    this.ambienceDuckGain = this.createGain();
    this.ambienceDuckGain.connect(this.buses.ambience);
    // CAMPAIGN VO: dialogue bus for campaign voice-over. Routed through master so
    // the existing master volume/mute apply; its own fader is surfaced in the Audio tab.
    this.buses.dialogue = this.createBus('master');
    this.initDialogueChain();
    this.buses.master?.connect(this.ctx.destination);
    this.startHum();
    this.initSamples();
    this.syncGains();
    this.started = true;
  }

  /** Build the comms radio chain (band-pass + soft compression) and the dialogue
   * input node the comms panel feeds. Routing (wet/dry) follows settings.radioFilter. */
  private initDialogueChain(): void {
    if (!this.ctx || !this.buses.dialogue) return;
    this.dialogueInputGain = this.createGain();
    this.radio = createRadioChain(this.ctx);
    this.radio.output.connect(this.buses.dialogue);
    this.radioOn = this.settings.radioFilter;
    this.routeDialogue();
  }

  /** (Re)connect the dialogue input either through the radio chain (filtered) or
   * straight to the dialogue bus (bypass). Idempotent per routing state. */
  private routeDialogue(): void {
    const input = this.dialogueInputGain;
    const bus = this.buses.dialogue;
    if (!input || !bus) return;
    input.disconnect();
    if (this.radioOn && this.radio) input.connect(this.radio.input);
    else input.connect(bus);
  }

  /** Lazy-load the sampled SFX once the context exists (called from resume, i.e.
   * after the user gesture). Falls through to procedural tones if buffers are absent. */
  private initSamples(): void {
    if (!this.ctx || !this.buses.sfx || !this.buses.ui || !this.ambienceDuckGain) return;
    this.samples = new SfxSampleBank(
      this.ctx,
      { sfx: this.buses.sfx, ui: this.buses.ui, ambience: this.ambienceDuckGain },
      (missing) => reportMissingAudio(missing),
    );
  }

  private createGain(): GainNode {
    return this.ctx!.createGain();
  }

  private createBus(parent: BusName): GainNode {
    const gain = this.createGain();
    gain.connect(this.buses[parent]!);
    return gain;
  }

  private startHum(): void {
    if (!this.ctx) return;
    const osc = this.ctx.createOscillator();
    const filter = this.ctx.createBiquadFilter();
    const gain = this.ctx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.value = HUM_BASE_HZ;
    filter.type = 'lowpass';
    filter.frequency.value = 320;
    gain.gain.value = 0;
    osc.connect(filter).connect(gain).connect(this.ambienceDuckGain ?? this.buses.ambience!);
    osc.start();
    this.humOsc = osc;
    this.humGain = gain;
    this.humFilter = filter;
  }

  private syncGains(): void {
    this.setBusGain('master', this.settings.muted ? 0 : this.settings.master);
    this.setBusGain('music', this.settings.music);
    this.setBusGain('sfx', this.settings.sfx);
    this.setBusGain('ui', this.settings.ui);
    this.setBusGain('ambience', this.settings.ambience);
    this.setBusGain('dialogue', this.settings.dialogue);
  }

  private setBusGain(name: BusName, value: number): void {
    const gain = this.buses[name];
    if (!gain) return;
    gain.gain.value = Math.max(0, Math.min(1, value));
  }

  private blip(
    type: OscillatorType,
    hz: number,
    dur: number,
    peak: number,
    bus: BusName,
    endHz?: number,
  ): void {
    const ctx = this.ctx;
    const gainOut = this.buses[bus];
    if (!ctx || !gainOut || this.settings.muted) return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(hz, now);
    if (endHz !== undefined) osc.frequency.exponentialRampToValueAtTime(endHz, now + dur);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(peak, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + dur);
    osc.connect(gain).connect(gainOut);
    osc.start(now);
    osc.stop(now + dur + 0.02);
  }
}
